// src/pages/ProfileNextcloud.jsx
import { useState, useEffect, useCallback, useRef } from "react";
import { Cloud, CloudOff, Check, AlertCircle, ExternalLink, RefreshCw, Loader2, Lock, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import apiService from "../backend/api";

export default function ProfileNextcloud() {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState(null);
  const [loginUrl, setLoginUrl] = useState(null);
  const [localToken, setLocalToken] = useState(null);
  const pollIntervalRef = useRef(null);

  // Load Healthcloud status on mount
  const loadStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getNextcloudStatus();
      setStatus(data);
    } catch (err) {
      console.error("Error loading Healthcloud status:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [loadStatus]);

  // Start Login Flow v2
  const handleConnect = async () => {
    try {
      setConnecting(true);
      setError(null);

      const data = await apiService.initNextcloudLogin();

      if (data.loginUrl) {
        setLoginUrl(data.loginUrl);
        setLocalToken(data.localToken);

        // Open login URL in new tab
        window.open(data.loginUrl, "_blank");

        // Start polling for completion
        pollIntervalRef.current = setInterval(async () => {
          try {
            const pollResult = await apiService.pollNextcloudLogin(data.localToken);

            if (pollResult.status === "success") {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
              setLoginUrl(null);
              setLocalToken(null);
              setConnecting(false);
              loadStatus();
            }
          } catch (pollErr) {
            // Session expired or error
            if (pollErr.message.includes("expired") || pollErr.message.includes("SESSION_EXPIRED")) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
              setLoginUrl(null);
              setLocalToken(null);
              setConnecting(false);
              setError(t("profile.nextcloud.connectionExpired", "Verbindung abgelaufen. Bitte erneut versuchen."));
            }
          }
        }, 2000); // Poll every 2 seconds
      }
    } catch (err) {
      console.error("Error initializing Healthcloud login:", err);
      setError(err.message);
      setConnecting(false);
    }
  };

  // Cancel connection attempt
  const handleCancelConnect = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    setLoginUrl(null);
    setLocalToken(null);
    setConnecting(false);
  };

  // Disconnect Healthcloud
  const handleDisconnect = async () => {
    if (!window.confirm(t("profile.nextcloud.confirmDisconnect", "Healthcloud-Verbindung wirklich trennen? Neue Scans werden nicht mehr automatisch synchronisiert."))) {
      return;
    }

    try {
      setDisconnecting(true);
      setError(null);
      await apiService.disconnectNextcloud();
      loadStatus();
    } catch (err) {
      console.error("Error disconnecting Healthcloud:", err);
      setError(err.message);
    } finally {
      setDisconnecting(false);
    }
  };

  // Test connection
  const handleTestConnection = async () => {
    try {
      setError(null);
      const result = await apiService.testNextcloudConnection();
      if (result.status === "success") {
        alert(t("profile.nextcloud.connectionSuccess", "Verbindung erfolgreich!"));
      }
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Compact Header */}
      <div className="flex items-center gap-3 sm:gap-4">
        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#0f7699]/10 dark:bg-[#4fc3f7]/10 rounded-xl sm:rounded-2xl flex items-center justify-center text-[#0f7699] dark:text-[#4fc3f7]">
          <Cloud size={20} className="sm:hidden" /><Cloud size={24} className="hidden sm:block" />
        </div>
        <div>
          <h1 className="text-lg sm:text-2xl font-black text-[#0f7699] dark:text-[#4fc3f7] tracking-tight">
            {t("profile.nextcloud.title", "Healthcloud-Verbindung")}
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 font-medium mt-0.5 hidden sm:block">
            {t("profile.nextcloud.description", "Verbinden Sie Ihren Account mit der Healthcloud, um 3D-Scans automatisch in Ihrer Cloud zu speichern.")}
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#0f7699] dark:text-[#4fc3f7]" />
        </div>
      ) : (
      <>
      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-800 dark:text-red-300 font-medium">{t("profile.nextcloud.error", "Fehler")}</p>
            <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Subscription Required Notice */}
      {status && !status.canConnect && (
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
          <div className="bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-3">
            <div className="bg-amber-100 dark:bg-amber-900/40 rounded-full p-2">
              <Lock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="font-semibold text-amber-800 dark:text-amber-300">{t("profile.nextcloud.subscriptionRequired", "Kostenpflichtiges Abonnement erforderlich")}</p>
              <p className="text-sm text-amber-600 dark:text-amber-400">{t("profile.nextcloud.featureNotAvailable", "Diese Funktion ist in Ihrem aktuellen Plan nicht verfügbar")}</p>
            </div>
          </div>
          <div className="p-4 sm:p-6 space-y-4">
            <p className="text-sm sm:text-base text-slate-600 dark:text-slate-300">
              {t("profile.nextcloud.subscriptionDesc", "Die Healthcloud-Synchronisation steht exklusiv für zahlende Abonnenten zur Verfügung. Mit einem kostenpflichtigen Abo werden Ihre 3D-Scans automatisch in Ihre persönliche Healthcloud hochgeladen und sicher gespeichert.")}
            </p>
            <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-4">
              <h4 className="font-medium text-slate-800 dark:text-white mb-2">{t("profile.nextcloud.benefits", "Ihre Vorteile mit der Healthcloud:")}</h4>
              <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-300">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#0f7699] dark:text-[#4fc3f7] flex-shrink-0" />
                  <span>{t("profile.nextcloud.benefit1", "Automatische Synchronisation genehmigter 3D-Scans")}</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#0f7699] dark:text-[#4fc3f7] flex-shrink-0" />
                  <span>{t("profile.nextcloud.benefit2", "DSGVO-konforme Speicherung auf deutschen Servern")}</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#0f7699] dark:text-[#4fc3f7] flex-shrink-0" />
                  <span>{t("profile.nextcloud.benefit3", "Jederzeit Zugriff auf Ihre Daten — teilen und herunterladen")}</span>
                </li>
              </ul>
            </div>
            <a
              href="/pakete"
              className="w-full py-3 bg-[#0f7699] hover:bg-[#0f7699]/90 dark:bg-[#4fc3f7] dark:hover:bg-[#4fc3f7]/90 dark:text-slate-900 text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
            >
              <ShieldCheck className="w-5 h-5" />
              {t("profile.nextcloud.subscribeNow", "Jetzt Abo abschließen")}
            </a>
          </div>
        </div>
      )}

      {/* Connection Status Card */}
      {status?.canConnect && (
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
          {/* Status Header */}
          <div className={`px-4 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0 ${
            status.connected ? "bg-[#0f7699]/10 dark:bg-[#4fc3f7]/10 border-b border-[#0f7699]/20 dark:border-[#4fc3f7]/20" : "bg-slate-50 dark:bg-slate-700/50 border-b border-slate-100 dark:border-slate-700"
          }`}>
            <div className="flex items-center gap-3">
              {status.connected ? (
                <div className="bg-[#0f7699]/15 dark:bg-[#4fc3f7]/15 rounded-full p-2">
                  <Check className="w-5 h-5 text-[#0f7699] dark:text-[#4fc3f7]" />
                </div>
              ) : (
                <div className="bg-slate-200 dark:bg-slate-600 rounded-full p-2">
                  <CloudOff className="w-5 h-5 text-slate-500 dark:text-slate-400" />
                </div>
              )}
              <div>
                <p className={`font-semibold ${status.connected ? "text-[#0f7699] dark:text-[#4fc3f7]" : "text-slate-700 dark:text-slate-300"}`}>
                  {status.connected ? t("profile.nextcloud.connected", "Verbunden") : t("profile.nextcloud.notConnected", "Nicht verbunden")}
                </p>
                {status.connected && status.connectedAt && (
                  <p className="text-sm text-[#0f7699] dark:text-[#4fc3f7]">
                    {t("profile.nextcloud.connectedSince", "Verbunden seit {{date}}", { date: new Date(status.connectedAt).toLocaleDateString("de-DE") })}
                  </p>
                )}
              </div>
            </div>

            {status.connected && (
              <button
                onClick={handleTestConnection}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-[#0f7699] dark:text-[#4fc3f7] hover:bg-[#0f7699]/15 dark:hover:bg-[#4fc3f7]/15 rounded-lg transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                {t("profile.nextcloud.testConnection", "Verbindung testen")}
              </button>
            )}
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6">
            {status.connected ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3 text-slate-600 dark:text-slate-300">
                  <Cloud className="w-5 h-5 text-[#0f7699] dark:text-[#4fc3f7]" />
                  <span>
                    {t("profile.nextcloud.syncInfo", "Ihre 3D-Scans werden automatisch nach /3D-Scans/ synchronisiert.")}
                  </span>
                </div>

                <div className="pt-4 border-t border-slate-100 dark:border-slate-700 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{t("profile.nextcloud.url", "Healthcloud-URL")}</p>
                    <p className="text-slate-800 dark:text-white">{status.nextcloudUrl}</p>
                  </div>
                  <button
                    onClick={handleDisconnect}
                    disabled={disconnecting}
                    className="px-4 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    {disconnecting && <Loader2 className="w-4 h-4 animate-spin" />}
                    {t("profile.nextcloud.disconnect", "Verbindung trennen")}
                  </button>
                </div>
              </div>
            ) : connecting && loginUrl ? (
              <div className="space-y-4 text-center">
                <div className="flex items-center justify-center gap-3 text-slate-600 dark:text-slate-300">
                  <Loader2 className="w-5 h-5 animate-spin text-[#0f7699] dark:text-[#4fc3f7]" />
                  <span>{t("profile.nextcloud.waitingAuth", "Warte auf Authentifizierung in der Healthcloud...")}</span>
                </div>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {t("profile.nextcloud.newWindowOpened", "Ein neues Fenster wurde geöffnet. Bitte melden Sie sich dort bei der Healthcloud an und erlauben Sie den Zugriff.")}
                </p>
                <div className="flex items-center justify-center gap-3">
                  <a
                    href={loginUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#0f7699] dark:text-[#4fc3f7] hover:text-[#0a5f7a] dark:hover:text-[#81d4fa] flex items-center gap-2"
                  >
                    {t("profile.nextcloud.reopenWindow", "Fenster erneut öffnen")}
                    <ExternalLink className="w-4 h-4" />
                  </a>
                  <button
                    onClick={handleCancelConnect}
                    className="text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                  >
                    {t("Abbrechen", "Abbrechen")}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-slate-600 dark:text-slate-300">
                  {t("profile.nextcloud.connectDesc", "Verbinden Sie Ihre Healthcloud, um 3D-Scans nach der Genehmigung automatisch in Ihren persönlichen Cloud-Speicher zu synchronisieren.")}
                </p>

                <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-4">
                  <h4 className="font-medium text-slate-800 dark:text-white mb-2">{t("profile.nextcloud.howItWorks", "So funktioniert es:")}</h4>
                  <ol className="space-y-2 text-sm text-slate-600 dark:text-slate-300">
                    <li className="flex items-start gap-2">
                      <span className="bg-[#0f7699]/15 dark:bg-[#4fc3f7]/15 text-[#0f7699] dark:text-[#4fc3f7] rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 text-xs font-bold">1</span>
                      <span>{t("profile.nextcloud.step1", "Klicken Sie auf \"Mit Healthcloud verbinden\"")}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="bg-[#0f7699]/15 dark:bg-[#4fc3f7]/15 text-[#0f7699] dark:text-[#4fc3f7] rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 text-xs font-bold">2</span>
                      <span>{t("profile.nextcloud.step2", "Melden Sie sich im neuen Fenster bei der Healthcloud an")}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="bg-[#0f7699]/15 dark:bg-[#4fc3f7]/15 text-[#0f7699] dark:text-[#4fc3f7] rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 text-xs font-bold">3</span>
                      <span>{t("profile.nextcloud.step3", "Erlauben Sie den Zugriff für 3D-Medico")}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="bg-[#0f7699]/15 dark:bg-[#4fc3f7]/15 text-[#0f7699] dark:text-[#4fc3f7] rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 text-xs font-bold">4</span>
                      <span>{t("profile.nextcloud.step4", "Ihre Scans werden ab sofort automatisch synchronisiert!")}</span>
                    </li>
                  </ol>
                </div>

                <button
                  onClick={handleConnect}
                  disabled={connecting}
                  className="w-full py-3 bg-[#0f7699] hover:bg-[#0f7699]/90 dark:bg-[#4fc3f7] dark:hover:bg-[#4fc3f7]/90 dark:text-slate-900 text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {connecting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {t("profile.nextcloud.connecting", "Verbindung wird hergestellt...")}
                    </>
                  ) : (
                    <>
                      <Cloud className="w-5 h-5" />
                      {t("profile.nextcloud.connectButton", "Mit Healthcloud verbinden")}
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-xl p-4 sm:p-6">
        <h3 className="font-semibold text-blue-800 dark:text-blue-300 mb-2">{t("profile.nextcloud.whatIsHealthcloud", "Was ist die Healthcloud?")}</h3>
        <p className="text-blue-700 dark:text-blue-400 text-sm">
          {t("profile.nextcloud.whatIsHealthcloudDesc", "Die Healthcloud ist Ihre persönliche Cloud auf unseren Servern. Mit der Synchronisation werden Ihre 3D-Scans automatisch in Ihrem Healthcloud-Ordner gespeichert, sobald Sie einen Scan genehmigen. So haben Sie jederzeit Zugriff auf Ihre Daten und können diese einfach teilen oder herunterladen.")}
        </p>
      </div>
      </>
      )}
    </div>
  );
}
